import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EmptyStateComponent } from '../../../../components/empty-state/empty-state.component';
import {
  IPrefillModalResult,
  PrefillModalComponent,
} from '../../../../components/prefill-modal/prefill-modal.component';
import { SkeletonComponent } from '../../../../components/skeleton/skeleton.component';
import { PrefillModel } from '../../../../models/prefill.model';
import { ConfirmService } from '../../../../services/confirm.service';
import { PrefillService } from '../../../../services/prefill.service';
import { TemplateService } from '../../../../services/template.service';

/** Opciones del filtro de estado. El API no pagina ni filtra este listado. */
type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Listado de prerellenados en tarjetas.
 *
 * Un prerellenado es un juego de valores por omision para una plantilla (el
 * nombre del servicio, la URL de acceso...). Nunca contiene credenciales.
 */
@Component({
  selector: 'app-prefills-page',
  imports: [EmptyStateComponent, FormsModule, NgSelectComponent, RouterLink, SkeletonComponent],
  templateUrl: './prefills-page.component.html',
  styleUrl: './prefills-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrefillsPageComponent implements OnInit {
  private readonly prefillService = inject(PrefillService);
  private readonly templateService = inject(TemplateService);
  private readonly modalService = inject(NgbModal);
  private readonly confirmService = inject(ConfirmService);

  protected readonly loading = this.prefillService.loading;
  protected readonly prefills = this.prefillService.prefills;
  protected readonly templates = this.templateService.templates;

  /**
   * `loaded` arranca en `false` y solo pasa a `true` cuando la peticion
   * TERMINA (con exito o con error). `loading` no basta: arranca en `false`,
   * asi que el primer render pintaria el estado vacio antes de saber si hay
   * prerellenados.
   */
  protected readonly loaded = this.prefillService.prefillsLoaded;
  protected readonly error = this.prefillService.prefillsError;

  protected readonly statusFilter = signal<StatusFilter>('all');
  protected readonly search = signal('');

  /**
   * Opciones fijas del filtro de estado. "Todos los estados" es un item REAL
   * de la lista (no un placeholder): el ng-select se comporta identico al
   * `<select>` nativo que reemplaza.
   */
  protected readonly statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Solo activos' },
    { value: 'inactive', label: 'Solo inactivos' },
  ];

  /** Nombre de plantilla por id, para pintarlo en la tarjeta. */
  private readonly templateNames = computed(() => {
    const map = new Map<string, string>();
    for (const template of this.templates()) map.set(template.templateId, template.name);
    return map;
  });

  protected readonly visiblePrefills = computed(() => {
    const term = this.search().trim().toLowerCase();
    const filter = this.statusFilter();
    const names = this.templateNames();

    return this.prefills().filter((prefill) => {
      const matchesStatus =
        filter === 'all' || (filter === 'active' ? prefill.status : !prefill.status);

      const templateName = (names.get(prefill.templateId) ?? '').toLowerCase();

      const matchesTerm =
        !term || prefill.name.toLowerCase().includes(term) || templateName.includes(term);

      return matchesStatus && matchesTerm;
    });
  });

  protected readonly activeCount = computed(
    () => this.prefills().filter((prefill) => prefill.status).length,
  );

  protected readonly skeletons = [0, 1, 2, 3, 4, 5];

  ngOnInit(): void {
    void this.load();
  }

  protected async load(): Promise<void> {
    await Promise.all([this.prefillService.getPrefills(), this.templateService.getTemplates()]);
  }

  /**
   * `null` llega cuando se limpia el ng-select con el boton de "x"; se
   * resuelve exactamente igual que elegir "Todos los estados".
   */
  protected onStatusFilterChange(value: StatusFilter | null): void {
    this.statusFilter.set(value ?? 'all');
  }

  protected onSearchChange(value: string): void {
    this.search.set(value);
  }

  protected templateName(prefill: PrefillModel): string {
    return this.templateNames().get(prefill.templateId) ?? 'Plantilla no disponible';
  }

  protected async create(): Promise<void> {
    const result = await this.openModal(null);
    if (!result) return;

    const created = await this.prefillService.createPrefill({
      templateId: result.templateId,
      name: result.name,
      contentType: result.contentType,
    });

    if (!created) return;

    if (result.file && created.presignFile) {
      await this.prefillService.uploadFile(created.presignFile, result.file);
    }

    await this.load();
  }

  protected async edit(prefill: PrefillModel, event?: Event): Promise<void> {
    event?.stopPropagation();

    const result = await this.openModal(prefill);
    if (!result) return;

    const updated = await this.prefillService.updatePrefill(prefill.prefillId, {
      name: result.name,
      status: result.status,
      contentType: result.contentType,
    });

    if (!updated) return;

    if (result.file && updated.presignFile) {
      await this.prefillService.uploadFile(updated.presignFile, result.file);
    }

    await this.load();
  }

  /** Borrado SOFT: el prerellenado se desactiva, no desaparece. */
  protected async deactivate(prefill: PrefillModel, event?: Event): Promise<void> {
    event?.stopPropagation();

    const confirmed = await this.confirmService.askDeactivate(
      'el prerellenado',
      `${prefill.name} · ${this.templateName(prefill)}`,
    );

    if (!confirmed) return;

    const deleted = await this.prefillService.deletePrefill(prefill.prefillId);
    if (deleted) await this.load();
  }

  private async openModal(prefill: PrefillModel | null): Promise<IPrefillModalResult | null> {
    const reference = this.modalService.open(PrefillModalComponent, {
      centered: true,
      backdrop: 'static',
      scrollable: true,
      ariaLabelledBy: 'ks-prefill-modal-title',
    });

    (reference.componentInstance as PrefillModalComponent).init(
      prefill,
      // El API rechaza con 400 las plantillas inactivas.
      this.templates().filter((template) => template.status),
    );

    try {
      return (await reference.result) as IPrefillModalResult;
    } catch {
      return null;
    }
  }
}
